import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Globe, Radio, RefreshCw, Headphones, Users, ArrowRight, Shield } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Hero } from '../components/Hero';
import { Features } from '../components/Features';
import { Testimonials } from '../components/Testimonials';
import { Contact } from '../components/Contact';
import { Footer } from '../components/Footer';
import { ChatWidget } from '../components/ChatWidget';
const popularServices = [{
  title: 'Web Hosting',
  description: 'Fast, secure shared hosting for personal or small business websites.',
  icon: Globe,
  link: '/services/hosting',
  color: 'fox',
  bgClass: 'bg-fox-500/10',
  textClass: 'text-fox-500',
  borderClass: 'group-hover:border-fox-500/50'
}, {
  title: 'Radio Station Rentals',
  description: 'Launch your own internet radio station with our fully managed streaming servers.',
  icon: Radio,
  link: '/services/radio',
  color: 'amber',
  bgClass: 'bg-amber-500/10',
  textClass: 'text-amber-500',
  borderClass: 'group-hover:border-amber-500/50'
}, {
  title: 'Reseller Accounts',
  description: 'Start your own hosting business with our white-label reseller plans.',
  icon: RefreshCw,
  link: '/services/reseller',
  color: 'fox',
  bgClass: 'bg-fox-500/10',
  textClass: 'text-fox-500',
  borderClass: 'group-hover:border-fox-500/50'
}, {
  title: 'Shoutcast Packages',
  description: 'Dedicated Shoutcast v2 DNAS servers with Centova Cast control panel.',
  icon: Headphones,
  link: '/services/shoutcast',
  color: 'purple',
  bgClass: 'bg-purple-500/10',
  textClass: 'text-purple-500',
  borderClass: 'group-hover:border-purple-500/50'
}, {
  title: 'Shoutcast Reseller',
  description: 'Resell Shoutcast hosting under your own brand with full control.',
  icon: Users,
  link: '/services/shoutcast-reseller',
  color: 'cyan',
  bgClass: 'bg-cyan-500/10',
  textClass: 'text-cyan-500',
  borderClass: 'group-hover:border-cyan-500/50'
}];
export function Home() {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} className="min-h-screen bg-slate-950" data-id="element-2474">
      <Navbar data-id="element-2475" />
      <main data-id="element-2476">
        <Hero data-id="element-2477" />

        {/* Popular Services Summary */}
        <section className="py-24 bg-slate-900 relative" id="services" data-id="element-2478">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.02]" data-id="element-2479" />
          <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2480">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2481">
              <h2 className="text-fox-500 font-semibold tracking-wide uppercase text-sm mb-3" data-id="element-2482">
                Our Services
              </h2>
              <h3 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6" data-id="element-2483">
                Everything you need to succeed online
              </h3>
              <p className="text-slate-400 text-lg" data-id="element-2484">
                Choose from our comprehensive suite of hosting and streaming
                solutions.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-2485">
              {popularServices.map((service, index) => <Link key={index} to={service.link} data-id="element-2486">
                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className={`group bg-slate-950 border border-slate-800 rounded-2xl p-8 h-full transition-all duration-300 hover:-translate-y-2 ${service.borderClass}`} data-id="element-2487">
                    <div className={`w-14 h-14 rounded-xl ${service.bgClass} flex items-center justify-center mb-6`} data-id="element-2488">
                      <service.icon className={service.textClass} size={28} data-id="element-2489" />
                    </div>
                    <h4 className="text-xl font-bold text-white mb-3 group-hover:text-fox-400 transition-colors" data-id="element-2490">
                      {service.title}
                    </h4>
                    <p className="text-slate-400 mb-6" data-id="element-2491">{service.description}</p>
                    <div className="flex items-center gap-2 text-sm font-medium text-fox-500 mt-auto" data-id="element-2492">
                      View Plans{' '}
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-2493" />
                    </div>
                  </motion.div>
                </Link>)}

              {/* SSL Link Card */}
              <Link to="/ssl" data-id="element-2494">
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.5
              }} className="group bg-slate-950 border border-slate-800 rounded-2xl p-8 h-full transition-all duration-300 hover:-translate-y-2 hover:border-emerald-500/50" data-id="element-2495">
                  <div className="w-14 h-14 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-6" data-id="element-2496">
                    <Shield className="text-emerald-500 w-7 h-7" data-id="element-2497" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3 group-hover:text-emerald-400 transition-colors" data-id="element-2498">
                    SSL Certificates
                  </h4>
                  <p className="text-slate-400 mb-6" data-id="element-2499">
                    Secure your website and build customer trust with our
                    premium SSL certificates.
                  </p>
                  <div className="flex items-center gap-2 text-sm font-medium text-emerald-500 mt-auto" data-id="element-2500">
                    View Certificates{' '}
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-2501" />
                  </div>
                </motion.div>
              </Link>
            </div>
          </div>
        </section>

        <Features data-id="element-2502" />
        <Testimonials data-id="element-2503" />
        <Contact data-id="element-2504" />
      </main>
      <Footer data-id="element-2505" />
      <ChatWidget data-id="element-2506" />
    </motion.div>;
}